import { cn } from "@/utils/cn";

interface SkeletonProps {
  /** Width of the skeleton (default: "100%") */
  width?: string | number;
  /** Height of the skeleton (default: "1rem") */
  height?: string | number;
  /** Border radius (default: "0.375rem" / 6px) */
  radius?: string | number;
  /** Custom className for additional styling */
  className?: string;
  /** Variant for different skeleton styles */
  variant?: "default" | "circular" | "rectangular" | "text";
  /** Animation type */
  animation?: "pulse" | "wave" | "none";
}

/**
 * Base Skeleton Component
 * A loading placeholder that adapts to different content types
 */
export const Skeleton = ({
  width = "100%",
  height = "1rem",
  radius,
  className,
  variant = "default",
  animation = "pulse",
}: SkeletonProps) => {
  // Determine border radius based on variant
  const getRadius = () => {
    if (radius !== undefined) return radius;
    switch (variant) {
      case "circular":
        return "50%";
      case "rectangular":
        return "0";
      case "text":
        return "0.25rem";
      default:
        return "0.375rem";
    }
  };

  // Determine animation class
  const getAnimationClass = () => {
    switch (animation) {
      case "pulse":
        return "animate-pulse";
      case "wave":
        return "animate-shimmer";
      case "none":
        return "";
      default:
        return "animate-pulse";
    }
  };

  return (
    <div
      className={cn(
        "bg-theme-skeleton-bg",
        getAnimationClass(),
        variant === "circular" && "rounded-full",
        className,
      )}
      style={{
        width: typeof width === "number" ? `${width}px` : width,
        height: typeof height === "number" ? `${height}px` : height,
        borderRadius: typeof getRadius() === "number" ? `${getRadius()}px` : getRadius(),
      }}
      role="status"
      aria-label="Loading..."
    />
  );
};

Skeleton.displayName = "Skeleton";
